import { getBaseRoute } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetDependencyTree, useGetTaskList } from "@/services";
import { Position } from "@xyflow/react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const useTaskHierarchy = () => {
  const {id, action, planId, planAction} = useParams();
  const navigate = useNavigate();
  const { projectData, planData } = useSelector(
    (state: RootState) => state.currentData
  );
  const basePath = getBaseRoute(useLocation().pathname);

  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState<"predecessors" | "successors">("predecessors");
  
  const isPredecessors = selectedTab == "predecessors";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${basePath}/${routes.projectOverview}`,
        name: `Project Overview`,
      },
      {
        link: `/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}`,
        name: `${projectData?.name} Project Details`
      },
      {
        link: `/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}/${planId}/${planAction}`,
        name: `${planData?.name} Details`
      },
      {
        link: "",
        name: 'View Task Hierarchy'
      },
    ],
    [projectData, planData]
  );

  const {data: taskList, isLoading: isLoadingTaskList } = useGetTaskList({
    projectId: id as string,
    planId: planId as string,
    queryParams: {
      enabled: !!(id && planId)
    }
  })

  const onTaskChange = (taskId: string) => {
    const task = taskList?.data?.find((data: any) => data?.taskId?.toString() == taskId);
    setSelectedTask(task);
  }

  const {data: treeTaskList, isLoading: isTreeLoading, refetch: refetchTreeTaskList, isRefetching: isTreeRefetching } = useGetDependencyTree({
    projectId: id as string,
    taskId: selectedTask?.taskId as string,
    filter: {
      direction: selectedTab
    },
    queryParams: {
      enabled: !!(id && selectedTask?.taskId)
    }
  })
  
  useEffect(() => {
    refetchTreeTaskList();
  }, [selectedTab, selectedTask])

  const transformData = (apiResponse: any) => {
    const nodes: any = [];
    const edges: any = [];
  
    // Spacing constants
    const X_OFFSET = 200; // Distance between columns
    const Y_OFFSET = 100; // Distance between rows
  
    // Track the current Y position for each column (level) to prevent overlapping
    const levelYCounter: any = {};

    const traverse = (item: any, parentId = null, level = 0) => {
      const currentId: any = `n${item?.taskId}`;
  
      // Initialize Y counter for this level if it doesn't exist
      if (levelYCounter[level] === undefined) levelYCounter[level] = 0;
  
      // 1. Create the Node
      nodes.push({
        id: currentId,
        type: 'custom',
        parentId,
        position: { 
          x: isPredecessors ? -(level * (X_OFFSET)) : level * (X_OFFSET), 
          y: levelYCounter[level] * Y_OFFSET 
        },
        data: { 
          label: item?.taskName || "",
          ...item,
        },
        hidden: parentId ? true : false,
        sourcePosition: isPredecessors ? Position.Left : Position.Right,
        targetPosition: isPredecessors ? Position.Right : Position.Left,
      });
  
      // Increment Y for this level so the next node at this level is lower
      levelYCounter[level] += 1;
  
      // 2. Create the Edge if there's a parent
      if (parentId) {
        edges.push({
          id: `e${parentId}-${currentId}`,
          source: parentId,
          target: currentId,
          hidden: parentId ? true : false
          // type: 'step', // 'step' creates the right-angle lines seen in your screenshot
        });
      }
  
      // 3. Recurse through children
      if (item?.children && item?.children?.length > 0) {
        item?.children?.forEach((child: any) => {
          traverse(child, currentId, level + 1);
        });
      }
    };
  
    // Start the recursion with the root data
    traverse(apiResponse);
  
    return { nodes, edges };
  };

  const getTreeData = useMemo(() => {
      return transformData(treeTaskList?.data);
  }, [treeTaskList])

  const onTabClick = (value: any) => {
    setSelectedTab(value);
  };

  const onBackClick = () => {
    navigate(`/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}/${planId}/${planAction}`)
  }

  return {
    breadcrumbData,
    isLoadingTaskList,
    taskList: taskList?.data || [],
    selectedTask,
    onTaskChange,
    isTreeLoading,
    getTreeData,
    isTreeRefetching,
    selectedTab,
    onTabClick,
    onBackClick,
    isPredecessors
  }

}

export default useTaskHierarchy;